import React, { useState, useEffect, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import Link from '../../components/Link';
import lpApi from '../../helpers/lpApi';

// ============================================================================
// CCPA/CPRA — Client Data Rights self-service.
//
// A logged-in user can exercise their privacy rights here:
//   • Right to Know / Access (export)   -> requestDataExport
//   • Right to Delete                    -> requestDataDeletion
//   • Right to Correct                   -> requestDataCorrection
//   • Right to Opt Out of Sale/Sharing   -> setDoNotSell (see /do-not-sell)
// and see the status of every request they have opened (myDsarRequests).
//
// All resolvers live on besthand-api and are reached through the `/lp-graphql`
// proxy via `lpApi` — exactly like the Custom Projects / admin DSAR pages. The
// resolvers are UNGATED (general privacy right), so they work regardless of the
// Large Projects flag. No GraphQL is duplicated in besthand-web.
// ============================================================================

const Q_MY = `
  query {
    myDsarRequests {
      status errorMessage
      dsarRequests {
        id type status description resultJson
        requestedAt processedAt rejectionReason
      }
    }
    myPrivacySettings { status doNotSellOptOut }
  }
`;

const M_EXPORT = `
  mutation ($description: String) {
    requestDataExport(description: $description) {
      status errorMessage dsarRequest { id type status }
    }
  }
`;

const M_DELETE = `
  mutation ($description: String) {
    requestDataDeletion(description: $description) {
      status errorMessage dsarRequest { id type status }
    }
  }
`;

const M_CORRECT = `
  mutation ($fields: String, $description: String) {
    requestDataCorrection(fields: $fields, description: $description) {
      status errorMessage dsarRequest { id type status }
    }
  }
`;

const TYPE_LABEL = {
  EXPORT: 'Access / Export my data',
  DELETE: 'Delete my account & data',
  CORRECT: 'Correct my data',
};

const S = {
  page: { maxWidth: 920, margin: '0 auto', padding: '8px 0 40px', fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f1214' },
  h1: { margin: '0 0 6px', fontSize: 28, fontWeight: 800 },
  hint: { color: '#6b7280', fontSize: 15, marginBottom: 24, lineHeight: 1.5 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 28 },
  card: { border: '1px solid #e7e9ec', borderRadius: 16, padding: '20px', boxShadow: '0 6px 18px rgba(15,18,20,0.03)', display: 'flex', flexDirection: 'column' },
  cardTitle: { fontSize: 17, fontWeight: 800, margin: '0 0 6px' },
  cardText: { color: '#6b7280', fontSize: 13.5, lineHeight: 1.5, margin: '0 0 14px', flex: 1 },
  textarea: { width: '100%', minHeight: 70, border: '1px solid #e7e9ec', borderRadius: 10, padding: 10, fontSize: 13.5, fontFamily: 'inherit', marginBottom: 12, resize: 'vertical' },
  btn: { background: '#e8232b', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  btnGhost: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 10, padding: '10px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  btnDisabled: { opacity: 0.55, cursor: 'not-allowed' },
  sectionTitle: { fontSize: 20, fontWeight: 800, margin: '8px 0 14px' },
  reqCard: { border: '1px solid #e7e9ec', borderRadius: 14, padding: '14px 18px', marginBottom: 12 },
  reqTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  reqName: { fontSize: 15.5, fontWeight: 800, margin: 0 },
  meta: { color: '#6b7280', fontSize: 13, marginTop: 4, lineHeight: 1.5 },
  badge: (kind) => {
    const map = {
      PENDING: { bg: '#fff3d6', fg: '#946200' },
      PROCESSING: { bg: '#eef2ff', fg: '#3730a3' },
      COMPLETED: { bg: '#e7f6ec', fg: '#137a37' },
      REJECTED: { bg: '#fde7e8', fg: '#c2151c' },
    };
    const c = map[kind] || { bg: '#eef0f2', fg: '#374151' };
    return { background: c.bg, color: c.fg, borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap' };
  },
  empty: { color: '#6b7280', fontSize: 14, padding: '16px 0' },
  notice: { background: '#f7f8fa', border: '1px solid #e7e9ec', borderRadius: 14, padding: '16px 18px', fontSize: 13.5, color: '#374151', lineHeight: 1.6, marginTop: 8 },
  link: { color: '#c2151c', fontWeight: 700, textDecoration: 'none' }, // A11Y 1.4.3: #e8232b is 4.46:1 on white; #c2151c is 6.14:1
};

function fmt(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch (e) { return String(d); }
}

function DataRights() {
  const [requests, setRequests] = useState([]);
  const [doNotSell, setDoNotSell] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const [correctText, setCorrectText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, errors } = await lpApi(Q_MY);
      if (errors && errors.length) {
        setMsg({ type: 'error', text: errors[0].message });
      } else {
        setRequests((data && data.myDsarRequests && data.myDsarRequests.dsarRequests) || []);
        setDoNotSell(!!(data && data.myPrivacySettings && data.myPrivacySettings.doNotSellOptOut));
      }
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (kind, query, variables) => {
    setBusy(kind);
    setMsg(null);
    try {
      const { data, errors } = await lpApi(query, variables);
      if (errors && errors.length) {
        setMsg({ type: 'error', text: errors[0].message });
      } else {
        const env = data && (data.requestDataExport || data.requestDataDeletion || data.requestDataCorrection);
        if (env && env.status === 200) {
          setMsg({ type: 'success', text: 'Your request was received. Track its status below.' });
          if (kind === 'CORRECT') setCorrectText('');
          await load();
        } else {
          setMsg({ type: 'error', text: (env && env.errorMessage) || 'Request failed.' });
        }
      }
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    }
    setBusy('');
  };

  const onExport = () => run('EXPORT', M_EXPORT, { description: null });
  const onDelete = () => {
    if (typeof window !== 'undefined' && !window.confirm('Request deletion of your account and personal data? An agent will review and process this request.')) return;
    run('DELETE', M_DELETE, { description: null });
  };
  const onCorrect = () => {
    if (!correctText.trim()) { setMsg({ type: 'error', text: 'Describe what should be corrected.' }); return; }
    run('CORRECT', M_CORRECT, { fields: correctText.trim(), description: null });
  };

  return (
    <div className={'subPageContainer'}>
      <Container>
        <div className={'subPageWrapper'} style={S.page}>
          <h1 style={S.h1}>Your privacy & data rights</h1>
          <p style={S.hint}>
            Under the California Consumer Privacy Act (CCPA/CPRA) and similar laws, you can access,
            correct, or delete the personal information BestHand holds about you. Submit a request
            below and track its status — we verify and fulfill requests within the required time.
          </p>

          {msg && (
            <div style={{ ...S.notice, borderColor: msg.type === 'error' ? '#f3c2c4' : '#bfe6cd', background: msg.type === 'error' ? '#fdf2f2' : '#f0faf3', marginBottom: 18 }}>
              {msg.text}
            </div>
          )}

          <div style={S.grid}>
            <div style={S.card}>
              <p style={S.cardTitle}>Access / Export</p>
              <p style={S.cardText}>Get a copy of the personal information we have collected about you.</p>
              <button
                type="button"
                style={{ ...S.btn, ...(busy === 'EXPORT' ? S.btnDisabled : {}) }}
                disabled={busy === 'EXPORT'}
                onClick={onExport}
              >
                {busy === 'EXPORT' ? 'Submitting…' : 'Request my data'}
              </button>
            </div>

            <div style={S.card}>
              <p style={S.cardTitle}>Correct</p>
              <p style={S.cardText}>Tell us what personal information is inaccurate and the correct value.</p>
              <textarea
                style={S.textarea}
                aria-label="Describe the information to correct"
                placeholder="e.g. My last name is misspelled — it should be 'Tremblay'."
                value={correctText}
                onChange={(e) => setCorrectText(e.target.value)}
              />
              <button
                type="button"
                style={{ ...S.btnGhost, ...(busy === 'CORRECT' ? S.btnDisabled : {}) }}
                disabled={busy === 'CORRECT'}
                onClick={onCorrect}
              >
                {busy === 'CORRECT' ? 'Submitting…' : 'Request correction'}
              </button>
            </div>

            <div style={S.card}>
              <p style={S.cardTitle}>Delete</p>
              <p style={S.cardText}>Request deletion of your account and personal data (subject to legal retention).</p>
              <button
                type="button"
                style={{ ...S.btnGhost, ...(busy === 'DELETE' ? S.btnDisabled : {}) }}
                disabled={busy === 'DELETE'}
                onClick={onDelete}
              >
                {busy === 'DELETE' ? 'Submitting…' : 'Request deletion'}
              </button>
            </div>
          </div>

          <div style={S.notice}>
            <strong>Do Not Sell or Share My Personal Information.</strong>{' '}
            BestHand does <strong>not</strong> sell or share your personal information for money or
            cross-context behavioral advertising. You can record your opt-out preference on the{' '}
            <Link to="/do-not-sell" style={S.link}>Do Not Sell or Share</Link> page.
            {doNotSell ? ' Your account is currently set to opt out.' : ''}
          </div>

          <h2 style={S.sectionTitle}>Your requests</h2>
          {loading ? (
            <p style={S.empty}>Loading…</p>
          ) : requests.length === 0 ? (
            <p style={S.empty}>You have not submitted any data requests yet.</p>
          ) : (
            requests.map((r) => (
              <div key={r.id} style={S.reqCard}>
                <div style={S.reqTop}>
                  <div>
                    <p style={S.reqName}>{TYPE_LABEL[r.type] || r.type}</p>
                    <p style={S.meta}>
                      Submitted {fmt(r.requestedAt)}
                      {r.processedAt ? ` · Processed ${fmt(r.processedAt)}` : ''}
                      {r.rejectionReason ? ` · Reason: ${r.rejectionReason}` : ''}
                    </p>
                  </div>
                  <span style={S.badge(r.status)}>{r.status}</span>
                </div>
                {r.type === 'EXPORT' && r.status === 'COMPLETED' && r.resultJson ? (
                  <p style={S.meta}>Your export is ready — contact support to receive the file securely.</p>
                ) : null}
              </div>
            ))
          )}
        </div>
      </Container>
    </div>
  );
}

export default DataRights;
